import { formatJobNumber, quoteListSchema, type QuoteListParams } from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { SortMenu } from '../components/ListControls.js'
import {
  Badge,
  ButtonGroup,
  Card,
  EmptyState,
  ErrorState,
  ListHead,
  Page,
  PageHeader,
  Pagination,
  QUOTE_STATUS_TONE,
  SearchBox,
  Segmented,
  SkeletonRows,
  StatStrip,
  StatTile,
  Toolbar,
  humanise,
  listRowClass,
} from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { formatShortDate, money, relativeTime } from '../lib/format.js'
import { useJobHref } from '../lib/job-overlay.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { useUrlState } from '../lib/url-state.js'

/**
 * Quotes, as work the business is waiting to win.
 *
 * Leads with what is out with customers and what it is worth, because a sent
 * quote nobody has answered is the one that wants a phone call. The views are
 * the questions — drafts to finish, quotes to chase, what was won and lost —
 * rather than the seven statuses one by one.
 *
 * A quote is written and sent from its job's Quote tab, so a row opens the job.
 */

type QuoteRow = RouterOutputs['quotes']['list']['items'][number]
type Status = QuoteListParams['status'][number]
type View = 'ALL' | 'DRAFT' | 'WAITING' | 'ACCEPTED' | 'LOST'

const VIEWS: Record<View, { label: string; status: Status[] }> = {
  ALL: { label: 'All', status: [] },
  DRAFT: { label: 'Draft', status: ['DRAFT'] },
  WAITING: { label: 'Awaiting reply', status: ['SENT', 'VIEWED'] },
  ACCEPTED: { label: 'Accepted', status: ['ACCEPTED'] },
  LOST: { label: 'Declined or expired', status: ['DECLINED', 'EXPIRED'] },
}

function viewOf(params: QuoteListParams): View | null {
  const key = [...params.status].sort().join(',')
  for (const [view, entry] of Object.entries(VIEWS) as Array<[View, (typeof VIEWS)[View]]>) {
    if ([...entry.status].sort().join(',') === key) return view
  }
  return null
}

const SORT_FIELDS = [
  { key: 'createdAt', label: 'Created' },
  { key: 'sentAt', label: 'Sent' },
  { key: 'number', label: 'Number' },
  { key: 'totalIncTax', label: 'Total' },
] as const

const GRID =
  'sm:grid-cols-[7.5rem_minmax(0,1.4fr)_minmax(0,1.3fr)_minmax(0,0.8fr)_10rem_6.5rem_6.5rem]'

export function Quotes() {
  const trpc = useTRPC()
  const [params, setParams] = useUrlState(quoteListSchema)

  const { data, isPending, error, refetch } = useQuery(trpc.quotes.list.queryOptions(params))
  const summary = useQuery(trpc.quotes.summary.queryOptions())

  const view = viewOf(params)
  const setView = (next: View) => setParams({ status: VIEWS[next].status, page: 1 })
  const filtered = Boolean(params.q) || view !== 'ALL'

  return (
    <Page>
      <PageHeader
        title="Quotes"
        description={
          summary.data
            ? `${money(summary.data.waiting.value)} out with customers`
            : 'Work the business is waiting to win'
        }
      />

      <StatStrip>
        <StatTile
          label="Awaiting reply"
          value={summary.data ? money(summary.data.waiting.value) : '·'}
          caption={summary.data ? `${summary.data.waiting.count} sent, no answer yet` : undefined}
          onClick={() => setView('WAITING')}
          active={view === 'WAITING'}
        />
        <StatTile
          label="Drafts"
          value={summary.data ? summary.data.drafts.count : '·'}
          caption={summary.data ? `${money(summary.data.drafts.value)} not sent yet` : undefined}
          onClick={() => setView('DRAFT')}
          active={view === 'DRAFT'}
        />
        <StatTile
          label="Won"
          value={summary.data ? money(summary.data.acceptedLast30.value) : '·'}
          tone={summary.data && summary.data.acceptedLast30.count > 0 ? 'green' : undefined}
          caption={summary.data ? `${summary.data.acceptedLast30.count} accepted in 30 days` : undefined}
          onClick={() => setView('ACCEPTED')}
          active={view === 'ACCEPTED'}
        />
        <StatTile
          label="Lost"
          value={summary.data ? summary.data.lostLast30 : '·'}
          caption="Declined or expired in 30 days"
          onClick={() => setView('LOST')}
          active={view === 'LOST'}
        />
      </StatStrip>

      <Card>
        <Toolbar className="border-b-0">
          <SearchBox
            value={params.q ?? ''}
            onChange={(q) => setParams({ q: q || undefined, page: 1 }, { replace: true })}
            placeholder="Number, title or client"
            className="max-sm:w-auto max-sm:min-w-0 max-sm:flex-1"
          />
          <Segmented<View>
            className="max-sm:order-2"
            label="Which quotes"
            value={view ?? 'ALL'}
            onChange={setView}
            options={(Object.keys(VIEWS) as View[]).map((key) => ({
              value: key,
              label: VIEWS[key].label,
            }))}
          />
          <ButtonGroup label="How the list reads" className="ml-auto rounded-lg max-sm:order-1">
            <SortMenu
              fields={SORT_FIELDS}
              sort={params.sort}
              dir={params.dir}
              onChange={(next) => setParams(next)}
            />
          </ButtonGroup>
        </Toolbar>
      </Card>

      <Card>
        {error ? (
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows />
        ) : data.items.length === 0 ? (
          <EmptyState
            icon="quotes"
            title={filtered ? 'No quotes match' : 'No quotes yet'}
            description={
              filtered
                ? 'Try another view or a shorter search.'
                : 'A quote is written on a job, on its Quote tab.'
            }
          />
        ) : (
          <>
            <ListHead className={GRID}>
              <span>Number</span>
              <span>Client</span>
              <span>Job</span>
              <span>Property</span>
              <span>Sent</span>
              <span className="text-right">Total</span>
              <span className="text-right">Status</span>
            </ListHead>
            <ul className="divide-y divide-ink-100">
              {data.items.map((quote) => (
                <li key={quote.id}>
                  <QuoteListRow quote={quote} />
                </li>
              ))}
            </ul>
          </>
        )}

        {data ? (
          <Pagination
            page={data.page}
            pageCount={data.pageCount}
            total={data.total}
            onChange={(page) => setParams({ page })}
          />
        ) : null}
      </Card>
    </Page>
  )
}

function QuoteListRow({ quote }: { quote: QuoteRow }) {
  const jobHref = useJobHref()
  const site = quote.job.site
  const place = site?.name ?? site?.address?.suburb ?? site?.address?.city ?? null
  const waiting = quote.status === 'SENT' || quote.status === 'VIEWED'

  return (
    <Link to={jobHref(quote.job.id)} className={cn(listRowClass, GRID)}>
      <span className="text-xs font-semibold tabular-nums text-ink-700">
        {quote.number}
        {/* Version only appears once there is more than one, so the common
            case stays uncluttered. */}
        {quote.version > 1 ? <span className="ml-1 font-normal text-ink-400">v{quote.version}</span> : null}
      </span>

      <span
        className="order-first min-w-0 basis-full text-sm font-medium break-words text-ink-900 sm:order-none sm:basis-auto sm:truncate"
        title={quote.job.client.name}
      >
        {quote.job.client.name}
      </span>

      <span
        className="min-w-0 truncate text-[11px] text-ink-500"
        title={quote.title ?? undefined}
      >
        <span className="tabular-nums">Job {formatJobNumber(quote.job)}</span>
        {quote.title ? ` · ${quote.title}` : ''}
      </span>

      <span className={cn('min-w-0 truncate text-xs text-ink-600', !place && 'hidden sm:block')}>
        {place ?? <span className="text-ink-400">—</span>}
      </span>

      <span className="truncate text-xs text-ink-600">
        {quote.sentAt ? (
          <>
            {formatShortDate(quote.sentAt)}
            {waiting ? <span className="text-[11px] text-ink-400"> · {relativeTime(quote.sentAt)}</span> : null}
          </>
        ) : (
          <span className="text-ink-400">Not sent</span>
        )}
      </span>

      <span className="ml-auto text-sm font-semibold tabular-nums text-ink-900 sm:ml-0 sm:text-right">
        {quote.totalIncTax ? money(quote.totalIncTax) : '—'}
      </span>

      <span className="sm:text-right">
        <Badge tone={QUOTE_STATUS_TONE[quote.status] ?? 'neutral'}>{humanise(quote.status)}</Badge>
      </span>
    </Link>
  )
}
